import { useState, useEffect, useRef } from 'react'
import { Link, router } from '@inertiajs/react'
import { AdminLayout } from '@/layouts/AdminLayout'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Search, X, Shield } from 'lucide-react'

interface User {
  id: number
  name: string
  phone: string
  email: string | null
  is_superadmin: boolean
  orgs_count: number
  created_at: string
}

interface Props {
  users: User[]
  meta: { current_page: number; total_pages: number; total_count: number }
  filters: { search?: string }
}

export default function Index({ users, meta, filters }: Props) {
  const [search, setSearch] = useState(filters.search || '')
  const didMount = useRef(false)

  useEffect(() => {
    if (!didMount.current) { didMount.current = true; return }
    const id = setTimeout(() => {
      const params: Record<string, string> = {}
      if (search) params.search = search
      params.page = '1'
      router.get('/admin/users', params, { preserveState: true, replace: true })
    }, 400)
    return () => clearTimeout(id)
  }, [search])

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold text-foreground">Usuarios</h1>
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Buscar por nombre, teléfono o email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-10"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Teléfono</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Orgs</TableHead>
                  <TableHead>Fecha</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12 text-muted-foreground">
                      No hay usuarios.
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <Link href={`/admin/users/${user.id}`} className="font-medium hover:underline">
                          {user.name}
                        </Link>
                        {user.is_superadmin && (
                          <Shield className="inline ml-1.5 h-3.5 w-3.5 text-primary" />
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{user.phone}</TableCell>
                      <TableCell className="text-muted-foreground">{user.email || '—'}</TableCell>
                      <TableCell>{user.orgs_count}</TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {new Date(user.created_at).toLocaleDateString('es-MX')}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {meta.total_pages > 1 && (
          <div className="flex items-center justify-center gap-2">
            {meta.current_page > 1 && (
              <Button variant="outline" size="sm" asChild>
                <Link href={`/users?page=${meta.current_page - 1}${search ? `&search=${search}` : ''}`}>
                  Anterior
                </Link>
              </Button>
            )}
            <span className="text-sm text-muted-foreground">
              Página {meta.current_page} de {meta.total_pages} ({meta.total_count} total)
            </span>
            {meta.current_page < meta.total_pages && (
              <Button variant="outline" size="sm" asChild>
                <Link href={`/users?page=${meta.current_page + 1}${search ? `&search=${search}` : ''}`}>
                  Siguiente
                </Link>
              </Button>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  )
}
